/**
 * API functions for bulk upload operations.
 */
import api from './api';
import axios from 'axios';

interface BulkUploadInitiateResponse {
  uploadId: string;
  batchId: string;
  bucket: string;
  key: string;
  presignedUrls: Array<{ partNumber: number; url: string }>;
}

interface BulkUploadCompleteResponse {
  batchId: string;
  key: string;
  status: string;
  message: string;
}

export interface BatchItemStatus {
  catalogId: string;
  filename: string;
  status: string;
  aiCatalogingStatus: string;
}

export interface BatchStatusResponse {
  batchId: string;
  total: number;
  cataloged: number;
  needsReview: number;
  failed: number;
  pending: number;
  items: BatchItemStatus[];
}

export async function initiateBulkUpload(
  file: File,
  catalogAliId: string,
  batchId?: string,
  orderIndex?: number,
): Promise<BulkUploadInitiateResponse> {
  const response = await api.post('/api/upload/bulk/initiate', {
    filename: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    catalogAliId,
    ...(batchId && { batchId }),
    ...(orderIndex !== undefined && { orderIndex }),
  });
  return response.data;
}

export async function uploadChunk(
  chunk: Blob,
  presignedUrl: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<string> {
  const response = await axios.put(presignedUrl, chunk, {
    headers: { 'Content-Type': 'application/octet-stream' },
    onUploadProgress: (event) => {
      if (onProgress && event.loaded && event.total) {
        onProgress(event.loaded, event.total);
      }
    },
  });
  return response.headers.etag;
}

export async function completeBulkUpload(
  uploadId: string,
  key: string,
  parts: Array<{ ETag: string; PartNumber: number }>,
): Promise<BulkUploadCompleteResponse> {
  const response = await api.post('/api/upload/bulk/complete', {
    uploadId,
    key,
    parts,
  });
  return response.data;
}

export async function getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
  const response = await api.get(`/api/upload/bulk/status/${batchId}`);
  return response.data;
}
